import {
	AxiosServiceClass,
	type AxiosServiceResponse,
	type EndpointConfig
} from '@/classes/axiosService.class';
import { momentProvider } from '@/services/clock.service';
import { StateService } from '@/services/state.service';
import { Helpers } from '@/common/helpers';
import _ from 'lodash';
import { FrameworkService } from '@/services/framework.service';

class UxSchedulesApiClass extends AxiosServiceClass {
	includeTimestampHeader = true;
	endpointConfig = [
		{
			key: 'ux-eventDetails-get',
			path: (eventId: any) => `ux/web-schedules/v2/events/${eventId}`,
			mock: 'context',
			mockPath: () => 'validation-badrequest-get'
		} as EndpointConfig,
		{
			key: 'ux-eventDetailsRegistration-get',
			path: (eventId: any) =>
				`ux/web-schedules/v2/events/${eventId}/registration`,
			mock: 'context',
			mockPath: () => 'validation-badrequest-get'
		} as EndpointConfig,
		{
			key: 'ux-classSchedules-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/schedules/classes?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'classSchedules-get'
		} as EndpointConfig,
		{
			key: 'ux-classSchedulesBrief-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/schedules/brief?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'classSchedulesBrief-get'
		} as EndpointConfig,
		{
			key: 'ux-myReservations-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/reservations?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'myReservations-get'
		} as EndpointConfig,
		{
			key: 'ux-myReservations-get-v2',
			path: (queryParams: any) =>
				'ux/web-schedules/v3/reservations?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'myReservations-get-v2'
		} as EndpointConfig,
		{
			key: 'ux-savedSearchesBrief-get',
			path: () => 'ux/web-schedules/v2/saved-searches',
			mock: 'function',
			mockPath: () => 'savedSearchesBrief-get'
		} as EndpointConfig,
		{
			key: 'ux-events-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/events?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'events-get'
		} as EndpointConfig,
		{
			key: 'ux-events-brief-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/events/brief?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'events-brief-get'
		} as EndpointConfig,
		{
			key: 'ux-nearbyEvents-get',
			path: (queryParams: any) =>
				`ux/web-schedules/v2/events/nearby/${queryParams.clubId}?` +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'nearbyEvents-get'
		} as EndpointConfig,
		{
			key: 'ux-booking-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/booking/schedules?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'booking-get'
		} as EndpointConfig,
		{
			key: 'ux-resource-booking-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/resources/booking/search?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'resource-booking-get'
		} as EndpointConfig,
		{
			key: 'ux-trainer-list-get',
			path: (queryParams: any) =>
				'ux/web-schedules/v2/trainers/list?' +
				Helpers.objectToQueryString(queryParams),
			mock: 'function',
			mockPath: () => 'trainer-list-get'
		} as EndpointConfig
	];

	getEventDetails(eventId: string) {
		return this.get(
			this.endpoint('ux-eventDetails-get', eventId),
			this.getConfig()
		);
	}

	getEventDetailsRegistration(eventId: string) {
		return this.get(
			this.endpoint('ux-eventDetailsRegistration-get', eventId),
			this.getConfig()
		);
	}

	searchClasses(
		searchDateStart: any,
		searchDateEnd: any,
		searchFilters: any,
		page: any
	) {
		// TODO: Update to specific types as part of task of converting this component to TypeScript
		if (!searchFilters) return null;
		if (searchFilters.locations.length === 0) {
			return Promise.resolve({
				// Don't do API request without a location (resolve without response.ok is handled by the components)
			});
		}

		const queryParams = {
			start: searchDateStart,
			end: searchDateEnd,
			tags: searchFilters.tags,
			locations: searchFilters.locations,
			isFree: searchFilters.freeOnly,
			organizers: searchFilters.organizers,
			descriptions: [],
			employeeIds: [],
			facet: searchFilters.facets.join(','),
			page: page,
			pageSize: 750
		};

		return this.get(
			this.endpoint('ux-classSchedules-get', queryParams),
			this.getConfig()
		);
	}

	searchClassesBrief(tags: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const today = momentProvider().startOf('day');
		const queryParams = {
			startDate: momentProvider().format(),
			endDate: today.clone().add(1, 'days').format('l'),
			tags: tags,
			location: StateService.getLocationName()
		};

		return this.get(
			this.endpoint('ux-classSchedulesBrief-get', queryParams),
			this.getConfig()
		);
	}

	getMyReservations(
		searchFilters: ReservationsFilters,
		endpoint: string
	): Promise<AxiosServiceResponse> {
		const memberData: MemberData[] = FrameworkService.data(
			'profile.members'
		) as MemberData[];
		const memberIds: number[] = [];

		_.forEach(searchFilters?.members, function(result) {
			_.find(memberData, function(r) {
				if (r.memberId === parseInt(result.split(':')[1], 10)) {
					return memberIds.push(r.memberId);
				} else if (r.firstName === result.split(':')[1]) {
					return memberIds.push(r.memberId);
				} else if (
					r.memberType === result.split(' ')[1] &&
					r.firstName === result.split(' ')[0].split(':')[1]
				) {
					return memberIds.push(r.memberId);
				}
			});
		});

		const queryParams: ReservationsQueryParams = {
			memberIds:
				memberIds.length === 0
					? StateService.getAllMemberIdArray()
					: memberIds,
			start: searchFilters.startDate,
			end: searchFilters.endDate,
			tags: searchFilters.tags,
			groupCamps: true,
			pageSize: 0
		};

		return this.get(this.endpoint(endpoint, queryParams), this.getConfig());
	}

	getMyReservationsBrief(pageSize: number): Promise<AxiosServiceResponse> {
		const today = momentProvider().startOf('day');
		const queryParams: ReservationsQueryParams = {
			memberIds: StateService.getAllMemberIdArray(),
			start: momentProvider().format('MM/DD/YYYY'),
			end: today
				.clone()
				.add(import.meta.env.VITE_MAX_DAYS_EVENTS_FORWARD, 'days')
				.format('MM/DD/YYYY'),
			pageSize: pageSize
		};

		return this.get(
			this.endpoint('ux-myReservations-get', queryParams),
			this.getConfig()
		);
	}

	getSavedSearches() {
		return this.get(
			this.endpoint('ux-savedSearchesBrief-get'),
			this.getConfig()
		);
	}

	getEvents(pageSize: number, searchFilters: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const queryParams = {
			start: searchFilters.startDate,
			end: searchFilters.endDate,
			tags: searchFilters.tags,
			status: 'confirmed',
			filters: 'tags:interestFormat,tags:age,location.name',
			isFree: searchFilters.freeOnly,
			locations: searchFilters.locations,
			pageSize: pageSize ? pageSize : 0
		};

		return this.get(
			this.endpoint('ux-events-get', queryParams),
			this.getConfig()
		);
	}

	getEventsBrief(searchFilters: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const queryParams = {
			start: searchFilters.startDate,
			end: searchFilters.endDate,
			tags: searchFilters.tags,
			status: 'confirmed',
			isFree: searchFilters.freeOnly,
			locations: searchFilters.locations
		};

		return this.get(
			this.endpoint('ux-events-brief-get', queryParams),
			this.getConfig()
		);
	}

	getNearbyEvents(searchFilters: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const queryParams = {
			clubId: StateService.getClubId(),
			start: searchFilters.startDate,
			end: searchFilters.endDate
		};

		return this.get(
			this.endpoint('ux-nearbyEvents-get', queryParams),
			this.getConfig()
		);
	}

	getBookingActivities(searchFilters: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const searchDate = searchFilters.date || null;

		const queryParams = {
			clubId: StateService.getClubId(),
			upcs: searchFilters.upcs,
			activityIds: searchFilters.service,
			organizers: searchFilters.organizers,
			date: searchDate,
			tags: searchFilters.tags,
			context: searchFilters.context
		};

		return this.get(
			this.endpoint('ux-booking-get', queryParams),
			this.getConfig()
		);
	}

	getResourceBookingData(searchFilters: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const queryParams = {
			homeClub: StateService.getClubId(),
			clubId: searchFilters.clubId || StateService.getClubId(),
			sport: searchFilters.sport || null,
			date:
				searchFilters.date ||
				momentProvider().startOf('day').format('YYYY-MM-DD'),
			startTime: searchFilters.startTime || null,
			duration: searchFilters.duration || null,
			resources: searchFilters.resources || null
		};

		return this.get(
			this.endpoint('ux-resource-booking-get', queryParams),
			this.getConfig()
		);
	}

	getTrainerList(searchFilters: any) {
		// TODO: Update to specific type as part of task of converting this component to TypeScript
		const queryParams = {
			clubId: StateService.getClubId(),
			activityIds: searchFilters.service,
			trainers: searchFilters.organizers
		};

		return this.get(
			this.endpoint('ux-trainer-list-get', queryParams),
			this.getConfig()
		);
	}
}

export interface facetValue {
	value: string;
}

export interface FacetData {
	filter: string;
	values: facetValue[];
}

export interface ReservationsFilters {
	tags: string[];
	locations: string[];
	organizers: string[];
	facets: string[];
	freeOnly: boolean;
	chiclets: Chiclet[];
	members: string[];
	reservationType: string[];
	startDate: string;
	endDate: string;
}

export interface Chiclet {
	name: string;
	value: string;
	type: string;
	disabled: boolean;
}

export interface MemberData {
	active: boolean;
	firstName: string;
	memberId: number;
	memberType: string;
	partyId: number;
}

export interface ReservationsQueryParams {
	memberIds?: number[];
	start?: string;
	end?: string;
	tags?: string[];
	pageSize?: number; // Note: Swagger shows both pageSize and pagesize
	customerId?: number;
	showSpa?: boolean;
	groupCamps?: boolean;
	orderBy?: string;
	page?: number;
}

export interface Reservation {
	start: string;
	end: string;
	cancelByDate?: string | null;
	eventId?: string;
	id?: string;
	reservationType?: string;
	eventName?: string;
	memberId: number;
	memberName?: string;
	location?: string;
	locationName?: string;
	clubId: number;
	appointmentId?: string;
	upc?: string;
	instructors?: ReservationInstructor[];
	registration: RegistrationInformation;
	category: ReservationCategory;
	groupingDetail?: GroupingDetail[];
	isProgressiveClass?: boolean; // TODO: Is this still being used?
	// Note: Below properties added to object by component (not in API response)
	startMoment: moment.Moment;
	endMoment: moment.Moment;
	locationArray?: string[];
	url: string;
	editCta?: Cta;
	cancelCta?: Cta;
	clubPhone?: string;
	clubEmail?: string;
}

export interface GroupingDetail {
	label: string;
	details: string;
}

export interface ReservationInstructor {
	name?: string;
	subFor?: string;
}

export interface RegistrationInformation {
	hasRegistration: boolean;
	hasWaitlist: boolean;
	remainingSpots: number;
	totalWaitlisted: number;
	waitlistCapacity?: number;
	clubPhone?: string;
	necessity?: string;
	registerCta: Cta;
	stats?: string[];
	notifications?: RegistrationNotification[];
	registeredMembers?: RegisteredMember[];
	unregisteredMembers?: UnregisteredMember[];
	rules?: {
		[name: string]: RuleExportResponse;
	} | null;
}

export enum ReservationCategory {
	Class = 'class',
	Event = 'event',
	Court = 'court',
	Spa = 'spa',
	Camp = 'camp',
	Other = 'other'
}

export interface Cta {
	type: CtaType;
	method: CtaMethod;
	url?: string;
	secondaryUrl?: string;
	text?: string;
	disabled: boolean;
	priority: number;
	registrationId?: string;
	isExternal: boolean;
	registrationMode?: string;
}

enum CtaType {
	Register = 'register',
	Edit = 'edit',
	Cancel = 'cancel'
}

enum CtaMethod {
	Online = 'online',
	Phone = 'phone',
	Club = 'club'
}

export interface RegistrationNotification {
	type: ValidationError;
	message?: string;
}

enum ValidationError {
	Unknown = 'Unknown',
	AdultMinor = 'AdultMinor',
	AgeLimit = 'AgeLimit',
	AlreadyPaid = 'AlreadyPaid',
	AlreadyRegistered = 'AlreadyRegistered',
	AlwaysFails = 'AlwaysFails',
	CallToCancel = 'CallToCancel',
	DayPassUser = 'DayPassUser',
	DocumentAcceptance = 'DocumentAcceptance',
	EventNotFound = 'EventNotFound',
	ExistingReservation = 'ExistingReservation',
	FreeOnly = 'FreeOnly',
	InvalidDuration = 'InvalidDuration',
	MaxIntervals = 'MaxIntervals',
	MaxReservationPerDay = 'MaxReservationPerDay',
	MemberError = 'MemberError',
	MinDuration = 'MinDuration',
	NoCancelPaidEvent = 'NoCancelPaidEvent',
	NoRules = 'NoRules',
	NoPrivilege = 'NoPrivilege',
	NotAuthenticated = 'NotAuthenticated',
	NotEditable = 'NotEditable',
	NotRegistrable = 'NotRegistrable',
	OnlineCancellationNotAvailable = 'OnlineCancellationNotAvailable',
	OnlineRegistrationNotAvailable = 'OnlineRegistrationNotAvailable',
	OutOfMembershipLevel = 'OutOfMembershipLevel',
	OutsideResourceHours = 'OutsideResourceHours',
	Past = 'Past',
	RegistrationNotFound = 'RegistrationNotFound',
	RegistrationPrivilege = 'RegistrationPrivilege',
	RosterLimit = 'RosterLimit',
	SixtyDayAlreadyInRoster = 'SixtyDayAlreadyInRoster',
	SixtyDayConsult = 'SixtyDayConsult',
	SpotSelection = 'SpotSelection',
	SpotUnavailable = 'SpotUnavailable',
	TooLate = 'TooLate',
	TooLateEdit = 'TooLateEdit',
	TooLateToCancel = 'TooLateToCancel',
	TooSoon = 'TooSoon',
	SanctionCancel = 'SanctionCancel'
}

export interface RegisteredMember {
	id: number;
	attendeeId?: string;
	spot?: string;
	spotWaitlist?: number;
	name?: string;
	editCtas?: Cta[];
	cancelCtas?: Cta[];
}

export interface UnregisteredMember {
	id: number;
	attendeeId?: string;
	spot?: string;
	spotWaitlist?: number;
	name?: string;
	isEligible: boolean;
	reason?: string;
	age: number;
}

export interface RuleExportResponse {
	displayOnFrontEnd?: boolean;
	errorMessage?: string;
}

export const UxSchedulesApi = new UxSchedulesApiClass();
